import { createSentryMonitor, Monitor } from '@codefied/payments-components/monitoring';
import {
  browserTracingIntegration,
  captureException,
  Event,
  extraErrorDataIntegration,
  init,
  setUser,
  SeverityLevel,
  thirdPartyErrorFilterIntegration,
} from '@sentry/react';

import { Codeowners } from '../../consts/codeowners';

function isInput(target?: HTMLElement): target is HTMLInputElement {
  return target?.nodeName.toLowerCase() === 'input';
}

function getBreadCrumbMessage(target: HTMLElement | undefined | HTMLInputElement) {
  const textContent = target?.innerText;

  return isInput(target) ? target?.labels?.[0]?.textContent : textContent;
}

const breadCrumbCategories = ['ui.click', 'ui.input'];

const ERROR_FINGERPRINT_RULES = [
  {
    includes: ['Minified Material-UI error'],
    fingerprint: ['minified-material-ui-error'],
  },
  {
    includes: ['已取消'],
    fingerprint: ['react-lazy-load-error'],
  },
  {
    includes: ["Cannot read properties of undefined (reading 'places')"],
    fingerprint: ['google-maps-api-load-error'],
  },
  {
    includes: ['Google Maps JavaScript API could not load'],
    fingerprint: ['google-maps-api-load-error'],
  },
  {
    includes: ['PlacesService'],
    fingerprint: ['google-maps-api-load-error'],
  },
  {
    includes: ['Cannot read properties of undefined', 'root'],
    fingerprint: ['react-root-access-error'],
  },
  {
    includes: ['Cannot read properties of undefined', 'default'],
    fingerprint: ['react-lazy-load-error'],
  },
  {
    includes: ['Cannot read property of undefined', 'default'],
    fingerprint: ['react-lazy-load-error'],
  },
  {
    includes: ['undefined is not an object', '.default'],
    fingerprint: ['react-lazy-load-error'],
  },
  {
    includes: ['Importing a module script failed'],
    fingerprint: ['react-lazy-load-error'],
  },
  {
    includes: ['Dynamic import returned undefined module'],
    fingerprint: ['react-lazy-load-error'],
  },
];

const getCustomFingerprint = (event: Event): string[] | undefined => {
  const errorMessage = event.exception?.values?.[0]?.value || event.message || '';
  const stackTrace = event.exception?.values?.[0]?.stacktrace?.frames;

  // Check existing rules first
  const matchingRule = ERROR_FINGERPRINT_RULES.find((rule) =>
    rule.includes.every((pattern) => errorMessage.includes(pattern))
  );

  if (matchingRule) {
    return matchingRule.fingerprint;
  }

  // payments-components is bundled into the online-booking chunks, so its stacks always end in an online-booking
  // file. A report it stamped (`pc_version`) and already fingerprinted keeps that fingerprint.
  if (event.tags?.pc_version && event.fingerprint?.length) {
    return undefined;
  }

  // Enhanced fingerprinting for app errors
  if (stackTrace && stackTrace.length > 0) {
    const topFrame = stackTrace[stackTrace.length - 1];
    if (topFrame?.filename?.includes('online-booking')) {
      // Create fingerprint based on error type + top stack frame
      const errorType = event.exception?.values?.[0]?.type || 'Error';
      const location = `${topFrame.filename}:${topFrame.function || 'anonymous'}`;
      return [errorType, location];
    }
  }

  // For other errors, let Sentry use default grouping
  return undefined;
};

const IGNORED_ERRORS = [
  // Random plugins/extensions
  'top.GLOBALS',
  // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
  'originalCreateNotification',
  'canvas.contentDocument',
  'MyApp_RemoveAllHighlights',
  'http://tt.epicplay.com',
  "Can't find variable: ZiteReader",
  'jigsaw is not defined',
  'ComboSearch is not defined',
  'http://loading.retry.widdit.com/',
  'atomicFindClose',
  // Facebook borked
  'fb_xd_fragment',
  // Bing blocked
  'UET is not defined',
  // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
  // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
  'bmi_SafeAddOnload',
  'EBCallBackMessageReceived',
  // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
  'conduitPage',
  // Generic error code from errors outside the security sandbox
  // You can delete this if using raven.js > 1.0, which ignores these automatically.
  'Script error.',
  'fromLatLngToDivPixel',
  'lineItemType',
  "Can't find variable: analytics",
  // Add browser errors that are not related to our app
  'NS_ERROR_ABORT',
  'NS_ERROR_FILE_NO_DEVICE_SPACE',
  'NS_ERROR_FILE_CORRUPTED',
  // Browser API errors
  'ResizeObserver loop limit exceeded',
  // Network and request errors
  'Request aborted',
  'The network connection was lost',
  'Load failed',
  'ChunkLoadError',
  /Network\s*?Error/,
  /failed with [4,5]\d{2} code/,
  /failed with status code [4,5]\d{2}/,
  'Failed to fetch',
  /**
   * Thrown when firefox prevents an add-on from refrencing a DOM element that has been removed.
   * This can also be filtered by enabling the browser extension inbound filter
   */
  "TypeError: can't access dead object",
  /**
   * React internal error thrown when something outside react modifies the DOM
   * This is usually because of a browser extension or Chrome's built-in translate
   */
  "NotFoundError: Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node.",
  "NotFoundError: Failed to execute 'insertBefore' on 'Node': The node before which the new node is to be inserted is not a child of this node.",
  'chain is not set up',
  // Ignore timeout errors
  /timeout/i,
  // Ignore ad blocker errors
  'Blocked by Adguard',
  'Resource blocked by content blocker',
  // MS Outlook plugin error
  'Object Not Found Matching Id',
  // Browser extension crypto wallet errors
  'isDefaultWallet',
  /chrome-extension:\/\/[a-z0-9]+\//,
];

export const setupSentry = () => {
  if (window.SENTRY?.enabled) {
    init({
      ignoreErrors: IGNORED_ERRORS,
      allowUrls: [/.*housecallpro.*/, /.*housecallpro-staging.*/, /.*housecallpro-preprod.*/, /.*housecallpro-qa.*/],
      debug: (typeof HC_ENV_NODE_ENV !== 'undefined' ? HC_ENV_NODE_ENV : 'development') !== 'production',
      initialScope: {
        tags: { codeowners: Codeowners.CUSTOMER_INTAKE },
      },
      tracePropagationTargets: ['https://pro.housecallpro-staging.com', 'https://pro.housecallpro.com'],
      integrations: (integrations) => [
        ...integrations,
        thirdPartyErrorFilterIntegration({
          filterKeys: ['housecallpro-online-booking-home-owner-flow'],
          behaviour: 'drop-error-if-exclusively-contains-third-party-frames',
        }),
        extraErrorDataIntegration({
          depth: 10,
        }),
        browserTracingIntegration(),
      ],
      release: typeof HC_ENV_REVISION !== 'undefined' ? HC_ENV_REVISION : 'unknown',
      normalizeDepth: 11,
      dsn: 'https://47f06c5f14e3a4bd83da2b94b1362ac0@o284669.ingest.sentry.io/4506337279279104',
      environment: window.SENTRY?.environment,
      beforeSend(event) {
        const customFingerprint = getCustomFingerprint(event);
        if (customFingerprint) {
          const nodeEnv = typeof HC_ENV_NODE_ENV !== 'undefined' ? HC_ENV_NODE_ENV : 'development';
          if (nodeEnv !== 'production') {
            // eslint-disable-next-line no-console
            console.log(
              'Sentry: Applied custom fingerprint:',
              customFingerprint,
              'for error:',
              event.exception?.values?.[0]?.value
            );
          }
          return {
            ...event,
            fingerprint: customFingerprint,
          };
        }
        return event;
      },
      beforeBreadcrumb(breadcrumb, hint) {
        try {
          if (breadcrumb.message?.includes('bam.nr-data.net')) {
            return null;
          }

          if (breadcrumb.category && breadCrumbCategories.includes(breadcrumb.category)) {
            const target = hint?.event.target as HTMLElement | undefined;

            return {
              ...breadcrumb,
              level: 'debug' as SeverityLevel,
              message: getBreadCrumbMessage(target) ?? breadcrumb.message,
              data: {
                className: target?.className,
                htmlTag: target?.nodeName,
                id: target?.id,
                dataTestId: target?.getAttribute('data-testid'),
              },
            };
          }

          return breadcrumb;
        } catch {
          return breadcrumb;
        }
      },
    });
  }
};

/**
 * Routes payments-components failures to this app's own Sentry client. Call right after `setupSentry()`
 * and before anything mounts. Sentry only: there is no RUM in online booking. When Sentry is not
 * enabled there is no client, so a registered Monitor drops the reports.
 */
export const setupPaymentsMonitoring = () => Monitor.setMonitor(createSentryMonitor({ captureException }));

export const sentryIdentifyUser = ({ email }: { email: string }) => {
  setUser({
    email,
  });
};
